import React from 'react';
import { Check, X, Shield, Bell, FileText, ArrowRight, AlertTriangle } from 'lucide-react';

const ICON_MAP = {
  check: Check,
  x: X,
  shield: Shield,
  bell: Bell,
  file: FileText,
  arrow: ArrowRight,
};

const TYPE_COLORS = {
  approval: 'text-emerald-400 bg-emerald-400/10',
  rejection: 'text-amber-400 bg-amber-400/10',
  'risk-resolved': 'text-emerald-400 bg-emerald-400/10',
  'meeting-reminder': 'text-indigo-400 bg-indigo-400/10',
  'meeting-notes': 'text-indigo-400 bg-indigo-400/10',
  'task-completed': 'text-emerald-400 bg-emerald-400/10',
  'follow-up': 'text-indigo-400 bg-indigo-400/10',
  'new-email': 'text-indigo-400 bg-indigo-400/10',
  'email-handled': 'text-emerald-400 bg-emerald-400/10',
};

const TYPE_COLORS_LIGHT = {
  approval: 'text-emerald-600 bg-emerald-600/10',
  rejection: 'text-amber-600 bg-amber-600/10',
  'risk-resolved': 'text-emerald-600 bg-emerald-600/10',
  'meeting-reminder': 'text-indigo-600 bg-indigo-600/10',
  'meeting-notes': 'text-indigo-600 bg-indigo-600/10',
  'task-completed': 'text-emerald-600 bg-emerald-600/10',
  'follow-up': 'text-indigo-600 bg-indigo-600/10',
  'new-email': 'text-indigo-600 bg-indigo-600/10',
  'email-handled': 'text-emerald-600 bg-emerald-600/10',
};

const formatTime = (timestamp) => {
  const now = Date.now();
  const diff = Math.floor((now - timestamp) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const NotificationDropdown = ({ notifications, isDark, onClose }) => {
  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const hoverBg = isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-black/[0.03]';
  const bg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  return (
    <div
      className={`${bg} absolute top-12 right-0 rounded-2xl py-2 shadow-xl border ${divider}`}
      style={{ width: '320px', maxHeight: '420px' }}
      data-testid="notification-dropdown"
    >
      <div className={`flex items-center justify-between px-4 py-2 border-b ${divider}`}>
        <p className={`text-sm font-semibold ${textPrimary}`}>Notifications</p>
        <button
          onClick={onClose}
          className={`p-1 rounded-lg ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'}`}
          data-testid="notification-close"
        >
          <X className={`w-3.5 h-3.5 ${textSecondary}`} />
        </button>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: '360px' }}>
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell className={`w-5 h-5 mx-auto mb-2 ${textSecondary}`} />
            <p className={`text-xs ${textSecondary}`}>No notifications yet</p>
            <p className={`text-[10px] mt-0.5 ${textSecondary}`}>Events will appear as the day progresses</p>
          </div>
        ) : (
          <div className="py-1">
            {notifications.map((n) => {
              const Icon = ICON_MAP[n.icon] || AlertTriangle;
              const colorClass = isDark
                ? (TYPE_COLORS[n.type] || 'text-white/50 bg-white/[0.06]')
                : (TYPE_COLORS_LIGHT[n.type] || 'text-slate-500 bg-black/[0.04]');

              return (
                <div
                  key={n.id}
                  className={`flex items-start gap-2.5 px-4 py-2.5 ${hoverBg} transition-colors cursor-default`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${colorClass}`}>
                    <Icon className="w-3 h-3" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[11px] font-medium leading-relaxed ${textPrimary}`}>{n.title}</p>
                    <p className={`text-[10px] mt-0.5 ${textSecondary}`}>{formatTime(n.timestamp)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;
